import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareQuote, Check, X, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin queue panel — shows all suspicious-but-not-auto-blocked
 * catchphrases pending human review. Approve makes the phrase live;
 * reject counts a strike against the user (3 strikes = banned).
 *
 * Mounted on the /admin page above the user-management table.
 */
export default function AdminCatchphraseQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decidingId, setDecidingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/catchphrases/queue`, {
        withCredentials: true,
      });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (queue_id, decision) => {
    setDecidingId(queue_id);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/catchphrases/${queue_id}/decide`,
        { decision },
        { withCredentials: true },
      );
      const verb = decision === "approve" ? "Approved" : "Rejected";
      const banned = r.data?.banned ? " · user banned (3 strikes)" : "";
      toast.success(`${verb}${banned}`);
      // Drop the row locally so the user sees instant feedback.
      setItems((prev) => prev.filter((x) => x.queue_id !== queue_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    } finally {
      setDecidingId(null);
    }
  };

  return (
    <section
      className="border border-white/10 bg-black/40 backdrop-blur-xl"
      data-testid="admin-catchphrase-queue"
    >
      <div className="p-4 border-b border-white/10 flex items-center gap-3">
        <MessageSquareQuote className="w-4 h-4 text-[#FFCC00]" />
        <div className="flex-1">
          <div className="label-kicker">Awaiting Review</div>
          <div className="font-heading text-xl uppercase">Catchphrase Queue</div>
        </div>
        <span
          className={`px-2 py-0.5 text-[10px] tracking-[0.25em] uppercase border ${
            items.length > 0
              ? "border-[#FFCC00] text-[#FFCC00] bg-[#FFCC00]/10"
              : "border-white/15 text-white/40"
          }`}
          data-testid="admin-queue-count"
        >
          {items.length} pending
        </span>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-queue-refresh"
          className="p-1.5 border border-white/10 hover:border-white/30 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading && items.length === 0 ? (
        <div className="p-8 text-center text-white/40 text-[11px] tracking-[0.3em] uppercase">
          Loading queue…
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-white/40 text-sm" data-testid="admin-queue-empty">
          Inbox zero. Nothing waiting on review.
        </div>
      ) : (
        <div className="divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
          {items.map((item) => {
            const busy = decidingId === item.queue_id;
            return (
              <div
                key={item.queue_id}
                className="px-4 py-3"
                data-testid={`admin-queue-row-${item.queue_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] tracking-wider uppercase text-white/40 mb-1 flex items-center gap-2">
                      <span>{item.user_name || "Unknown"}</span>
                      <span className="text-white/20">·</span>
                      <span>{new Date(item.submitted_at).toLocaleString()}</span>
                    </div>
                    <div className="font-heading text-base text-white break-words">
                      &quot;{item.text}&quot;
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => decide(item.queue_id, "approve")}
                      disabled={busy}
                      data-testid={`admin-queue-approve-${item.queue_id}`}
                      className={`p-2 border-2 transition-colors ${
                        busy
                          ? "border-white/10 text-white/30 cursor-not-allowed"
                          : "border-[#34C759] text-[#34C759] hover:bg-[#34C759] hover:text-black"
                      }`}
                      title="Approve"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => decide(item.queue_id, "reject")}
                      disabled={busy}
                      data-testid={`admin-queue-reject-${item.queue_id}`}
                      className={`p-2 border-2 transition-colors ${
                        busy
                          ? "border-white/10 text-white/30 cursor-not-allowed"
                          : "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
                      }`}
                      title="Reject (counts as a strike)"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
